import { joiResolver } from "@hookform/resolvers/joi";
import { AuthPartner, AuthPartnerInput } from "@happy-legal/graphql-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import Joi from "joi";
import { cloneDeep } from "lodash";
import React, { useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import HLCheckboxWithLabel from "../../../shared/components/Form/HLCheckboxWithLabel";
import HLTextField from "../../../shared/components/Form/HLTextField";
import { hasDirtyFields } from "../../../shared/helpers/hasDirtyFields";
import { AuthPartnerContext } from "../../../shared/providers/AuthPartnerProvider";
import { ADD_AUTH_PARTNER, UPDATE_AUTH_PARTNER } from "../../../shared/reducer";
import { notify } from "../../../shared/utils/notify";

type TAuthPartnersDialog = {
  id: string;
  handleCloseDialog: () => void;
};
const AuthPartnersDialog = ({ id, handleCloseDialog }: TAuthPartnersDialog) => {
  // Load state and reducer
  const { state, dispatch } = useContext(AuthPartnerContext);

  // Updating existing auth partner is id exists
  const isUpdating = !!id;

  // Load current auth partner from state, else use default
  const defaultValues = isUpdating
    ? cloneDeep(state).allAuthPartners.items.find(
        (authPartner) => authPartner.id === id
      )
    : { id, valid: true };

  // Other auth partner ids (filtering current)
  const otherAuthPartnerIds =
    state.allAuthPartners.items
      .map(({ id }) => id)
      .filter((authPartnerId) => authPartnerId !== id) || [];

  // Prevent entry of existing auth partner ids
  const schema = Joi.object({
    id: Joi.string()
      .invalid(...otherAuthPartnerIds)
      .messages({
        "any.invalid": "ID muss eindeutig sein",
        "string.empty": "ID ist erforderlich",
      }),
    valid: Joi.boolean(),
  });

  // Initiate RHF
  const methods = useForm<Partial<AuthPartner>>({
    mode: "all",
    defaultValues,
    resolver: joiResolver(schema),
  });
  const {
    formState: { isSubmitSuccessful },
  } = methods;

  // Bypass memory leak cause by not consitently mounting dialog
  useEffect(() => {
    if (!isSubmitSuccessful) return;
    handleCloseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const handleSubmit = (formData: Partial<AuthPartner>) => {
    if (isUpdating) {
      dispatch({
        type: UPDATE_AUTH_PARTNER,
        id,
        payload: { ...formData, valid: formData.valid ? true : false },
      });
    } else {
      notify(
        "Achtung! Sie werden den Private Key nur EINMAL sehen können, solange Sie auf dieser Seite bleiben."
      );

      dispatch({
        type: ADD_AUTH_PARTNER,
        payload: formData as AuthPartnerInput,
      });
    }
  };

  return (
    <Dialog open onClose={handleCloseDialog}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent>
            <HLTextField label="ID" name="id" margin="normal" required />
            <HLCheckboxWithLabel label="Gültig" name="valid" />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Abbrechen
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!hasDirtyFields(methods)}
            >
              Speichern
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AuthPartnersDialog;
